import { type } from 'arktype';

/**
 * High-level planning command models (tech spec: ai-chat-planning-mutations).
 *
 * Commands mutate assignment overrides on a plan instance through
 * `PlanningCommandsService`. Every command supports a dry run that returns the
 * would-be change set and resulting conflicts without persisting — the UI
 * preview dialog and the AI chat confirmation card consume the same payload.
 */

// `type('string')` + narrow instead of a regex type: the regex form infers the
// template-literal type `${number}-${number}-${number}`, which is not
// assignable to the plain-string dates used across the planning domain
// (ConflictResult, ScheduleMap).
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const isoDateSchema = type('string')
  .narrow((value, ctx) => (ISO_DATE_PATTERN.test(value) ? true : ctx.mustBe('an ISO date (YYYY-MM-DD)')))
  .configure({
    description: 'ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['2026-07-20'],
  });

const dryRunSchema = type('boolean').configure({
  description: 'When true, the command computes and returns a preview without persisting any change.',
  examples: [true],
});

const baseInstanceUpdatedAtSchema = type('string').configure({
  description:
    "Optimistic-concurrency token. Must match the instance's current `updatedAt` ISO timestamp; the server rejects the command with 409 when stale.",
  examples: ['2026-07-06T08:00:00.000Z'],
});

/**
 * Clear assignments in a date range, optionally narrowed to one employee
 * and/or one or more shift units. The scalar shift unit filter remains
 * supported for compatibility. Without filters the whole range is emptied.
 */
export const clearAssignmentsInputSchema = type({
  baseInstanceUpdatedAt: baseInstanceUpdatedAtSchema,
  'dryRun?': dryRunSchema,
  startDate: isoDateSchema,
  endDate: isoDateSchema,
  'employeeId?': type('string>=1').configure({
    description: 'Only remove assignments of this employee.',
    examples: ['emp_abc123'],
  }),
  'shiftUnitId?': type('string>=1').configure({
    description: 'Only remove assignments to this shift unit.',
    examples: ['sunit_abc123'],
  }),
  'shiftUnitIds?': type('string>=1')
    .array()
    .atLeastLength(1)
    .narrow((value, ctx) => {
      if (new Set(value).size !== value.length) {
        return ctx.mustBe('an array of unique shift unit IDs');
      }
      return true;
    })
    .configure({
      description: 'Only remove assignments to these shift units; the array must contain at least one unique shift unit ID.',
      examples: [['sunit_abc123', 'sunit_def456']],
    }),
})
  .narrow((value, ctx) => {
    if (value.shiftUnitId !== undefined && value.shiftUnitIds !== undefined) {
      return ctx.mustBe('either shiftUnitId or shiftUnitIds, not both');
    }
    return true;
  })
  .configure({ description: 'Payload for clearing assignments in a date range of a plan instance.' });

export type ClearAssignmentsInput = typeof clearAssignmentsInputSchema.t;

/** Assign one employee to one shift unit on a list of dates. */
export const assignEmployeeInputSchema = type({
  baseInstanceUpdatedAt: baseInstanceUpdatedAtSchema,
  'dryRun?': dryRunSchema,
  employeeId: type('string>=1').configure({
    description: 'Employee to assign.',
    examples: ['emp_abc123'],
  }),
  shiftUnitId: type('string>=1').configure({
    description: 'Shift unit the employee is assigned to.',
    examples: ['sunit_abc123'],
  }),
  dates: isoDateSchema
    .array()
    .atLeastLength(1)
    .configure({
      description: 'Concrete dates the assignment applies to.',
      examples: [['2026-07-21', '2026-07-22']],
    }),
}).configure({ description: 'Payload for assigning an employee to a shift unit on specific dates.' });

export type AssignEmployeeInput = typeof assignEmployeeInputSchema.t;

/**
 * Remove one employee's assignments on a list of dates, optionally narrowed
 * to a single shift unit.
 */
export const unassignEmployeeInputSchema = type({
  baseInstanceUpdatedAt: baseInstanceUpdatedAtSchema,
  'dryRun?': dryRunSchema,
  employeeId: type('string>=1').configure({
    description: 'Employee whose assignments are removed.',
    examples: ['emp_abc123'],
  }),
  dates: isoDateSchema
    .array()
    .atLeastLength(1)
    .configure({
      description: 'Dates on which the assignments are removed.',
      examples: [['2026-07-22']],
    }),
  'shiftUnitId?': type('string>=1').configure({
    description: 'Only remove the assignment to this shift unit; other assignments on the date stay.',
    examples: ['sunit_abc123'],
  }),
}).configure({ description: 'Payload for unassigning an employee on specific dates.' });

export type UnassignEmployeeInput = typeof unassignEmployeeInputSchema.t;

/** Exchange the assignments of two employees for every date in a range. */
export const swapAssignmentsInputSchema = type({
  baseInstanceUpdatedAt: baseInstanceUpdatedAtSchema,
  'dryRun?': dryRunSchema,
  employeeIdA: type('string>=1').configure({
    description: 'First employee of the swap.',
    examples: ['emp_abc123'],
  }),
  employeeIdB: type('string>=1').configure({
    description: 'Second employee of the swap.',
    examples: ['emp_def456'],
  }),
  startDate: isoDateSchema,
  endDate: isoDateSchema,
})
  .narrow((value, ctx) => {
    if (value.employeeIdA === value.employeeIdB) {
      return ctx.mustBe('two different employees');
    }
    return true;
  })
  .configure({ description: 'Payload for swapping the assignments of two employees across a date range.' });

export type SwapAssignmentsInput = typeof swapAssignmentsInputSchema.t;

/** Union of all override-mutating command inputs (stored on pending actions). */
export type PlanningCommandInput = ClearAssignmentsInput | AssignEmployeeInput | UnassignEmployeeInput | SwapAssignmentsInput;

/** One atomic assignment difference produced by a command. */
export const assignmentChangeSchema = type({
  date: isoDateSchema,
  employeeId: type('string').configure({
    description: 'Employee whose assignment changes.',
    examples: ['emp_abc123'],
  }),
  shiftUnitId: type('string').configure({
    description: 'Shift unit the change refers to.',
    examples: ['sunit_abc123'],
  }),
  action: type("'added' | 'removed'").configure({
    description: 'Whether the assignment is added or removed on this date.',
    examples: ['removed'],
  }),
}).configure({ description: 'A single assignment change (one employee, one shift unit, one date).' });

export type AssignmentChange = typeof assignmentChangeSchema.t;

/**
 * Arktype mirror of the {@link ConflictResult} interface for OpenAPI docs.
 * Keep in sync with `validation/conflict.types.ts`.
 */
export const commandConflictSchema = type({
  type: type(
    "'holiday' | 'closed_day' | 'absence' | 'duplicate' | 'overlap' | 'overnight_overlap' | 'skill_expired' | 'skill_missing' | 'skill_rating_below_minimum' | 'cross_team_double_booking' | 'employment_inactive' | 'shift_invalid_weekday'"
  ).configure({ description: 'Conflict type.', examples: ['absence'] }),
  date: isoDateSchema,
  personId: 'string',
  eventLabel: 'string',
  calendarId: 'string',
  'shiftUnitId?': 'string',
  'conflictingShiftUnitId?': 'string',
  'conflictingPlanInstanceId?': 'string',
  'conflictingTeamName?': 'string',
}).configure({ description: 'A conflict that would exist after the command is applied.' });

export const planningCommandSummarySchema = type({
  assignmentsAdded: type('number>=0').configure({
    description: 'Number of assignments the command adds.',
    examples: [4],
  }),
  assignmentsRemoved: type('number>=0').configure({
    description: 'Number of assignments the command removes.',
    examples: [12],
  }),
  affectedDates: isoDateSchema.array().configure({
    description: 'Dates with at least one change, ascending.',
    examples: [['2026-07-20', '2026-07-21']],
  }),
  affectedEmployees: type('string[]').configure({
    description: 'IDs of employees with at least one change.',
    examples: [['emp_abc123']],
  }),
}).configure({ description: 'Aggregated counts describing the effect of a planning command.' });

export type PlanningCommandSummary = typeof planningCommandSummarySchema.t;

/**
 * Dry-run preview of a planning command: the change list, the conflicts the
 * resulting schedule would have inside the affected range, and a summary.
 */
export const planningCommandPreviewSchema = type({
  changes: assignmentChangeSchema.array().configure({
    description: 'Every assignment change the command would perform.',
  }),
  conflicts: commandConflictSchema.array().configure({
    description: 'Conflicts the resulting schedule would contain inside the affected date range.',
  }),
  summary: planningCommandSummarySchema,
}).configure({ description: 'Preview of a planning command (identical for dry run and apply).' });

export type PlanningCommandPreview = typeof planningCommandPreviewSchema.t;

/**
 * Response of every planning command endpoint. `preview` is always present;
 * `applied` only when the command persisted (`dryRun` false). Applied
 * mutations include the complete canonical instance so clients can rebase
 * without a follow-up read racing the next local edit.
 */
export const planningCommandResponseSchema = type({
  dryRun: type('boolean').configure({
    description: 'True when the command only produced a preview.',
    examples: [true],
  }),
  preview: planningCommandPreviewSchema,
  'applied?': type({
    instanceId: type('string').configure({
      description: 'ID of the mutated plan instance.',
      examples: ['planinst_abc123'],
    }),
    updatedAt: type('string').configure({
      description: 'New `updatedAt` of the instance — the concurrency token for follow-up commands.',
      examples: ['2026-07-06T08:01:00.000Z'],
    }),
    instance: type('object').configure({
      description: 'Complete canonical plan instance after the mutation, including overrides, conflicts, status, and updatedAt.',
    }),
  }).configure({ description: 'Present when the command persisted changes.' }),
}).configure({ description: 'Outcome of a planning command: preview plus, when applied, the new instance fingerprint.' });

export type PlanningCommandResponse = typeof planningCommandResponseSchema.t;

/** Result snapshot stored on an executed pending action. */
export type PlanningCommandResult = Pick<PlanningCommandResponse, 'preview' | 'applied'>;

/** Remove a person's suppression for a specific date, restoring their person-plan default assignment. */
export const restoreDefaultInputSchema = type({
  baseInstanceUpdatedAt: baseInstanceUpdatedAtSchema,
  date: isoDateSchema,
  personId: type('string>=1').configure({
    description: 'Person whose default suppression should be cleared on this date.',
    examples: ['emp_abc123'],
  }),
}).configure({ description: "Payload for restoring a person's default assignment by clearing their suppression on a specific date." });

export type RestoreDefaultInput = typeof restoreDefaultInputSchema.t;

export interface RestoreDefaultResponse {
  instanceId: string;
  updatedAt: string;
}
